import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useMemo,
} from 'react';

import cn from 'classnames';

import styles from './slider.module.css';

type SliderProps = {
  stops?: boolean;
  absMin?: number;
  min: number;
  max: number;
  absMax?: number;
  leftLabel?: string;
  rightLabel?: string;
  labelClassName?: string;
  rightLabelClassName?: string;
  leftLabelClassName?: string;
  defaultValue?: number;
} & React.InputHTMLAttributes<HTMLInputElement>;

export const Slider = React.forwardRef<HTMLInputElement, SliderProps>(
  (props, ref) => {
    const {
      stops,
      absMin,
      min,
      max,
      absMax,
      leftLabel,
      rightLabel,
      defaultValue,
      disabled,
      labelClassName,
      rightLabelClassName,
      leftLabelClassName,
      ...inputProps
    } = props;

    const leftStop = useRef<HTMLDivElement>(null);
    const rightStop = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [internalValue, setInternalValue] = useState<number>(
      Number(defaultValue) || Number(inputProps.value) || min,
    );

    // keep internal state in sync with external state
    useEffect(() => {
      if (Number(inputProps.value) >= min && Number(inputProps.value) <= max) {
        setInternalValue(Number(inputProps.value));
      }
    }, [inputProps.value, setInternalValue, min, max]);

    // make fake bar relative sizes acurate
    const fakeLeftSize = useMemo(() => {
      if (absMin !== undefined) {
        return (100 * (min - absMin)) / ((absMax ?? max) - absMin);
      }
      return 0;
    }, [absMin, absMax, min, max]);

    const fakeRightSize = useMemo(() => {
      if (absMax !== undefined) {
        return (100 * (absMax - max)) / (absMax - (absMin ?? min));
      }
      return 0;
    }, [absMin, absMax, min, max]);

    const sliderSize = useMemo(
      () => 100 - fakeLeftSize - fakeRightSize,
      [fakeLeftSize, fakeRightSize],
    );

    // updates css variables for background bar color & thumb position
    // cross browser compatability
    useEffect(() => {
      if (containerRef.current) {
        const percentage = (100 * (internalValue - min)) / (max - min);
        containerRef.current.style.setProperty('--sliderVal', `${percentage}%`);
      }
    }, [containerRef, internalValue, inputProps.value, min, max]);

    // updates stop data attribute for labels
    useLayoutEffect(() => {
      if (stops && leftStop.current && typeof absMin === 'number') {
        leftStop.current.setAttribute('data-value', `${min.toLocaleString()}`);
      } else if (stops && leftStop.current) {
        leftStop.current.setAttribute('data-value', ``);
      }
      if (stops && rightStop.current && typeof absMax === 'number') {
        rightStop.current.setAttribute('data-value', `${max.toLocaleString()}`);
      } else if (stops && rightStop.current) {
        rightStop.current.setAttribute('data-value', ``);
      }
    }, [stops, min, max, absMin, absMax]);

    // check if mins and maxes make sense
    if (
      !(
        [absMin, min, max, absMax].filter(
          (val) => val !== undefined,
        ) as number[]
      ).every((val, i, arr) => i === 0 || val >= arr[i - 1])
    ) {
      return <div>Input Values in Incorrect Order</div>;
    }

    return (
      <div
        className={cn(styles.container, { [styles.disabled]: disabled })}
        ref={containerRef}
      >
        {leftLabel && (
          <span className={cn(labelClassName, leftLabelClassName)}>
            {leftLabel}
          </span>
        )}
        {absMin !== undefined && absMin !== min && (
          <div
            data-testid="fakeLeftBar"
            style={{ width: `${fakeLeftSize}%` }}
            className={cn(styles.fakeBar, styles.fakeLeftBar)}
          />
        )}
        <div
          className={cn(styles.sliderContainer, {
            [styles.hide]: sliderSize === 0,
          })}
          style={{
            width: `${sliderSize}%`,
          }}
        >
          {stops && (
            <div ref={leftStop} className={cn(styles.stops, styles.min)}></div>
          )}
          <input
            ref={ref}
            type="range"
            className={cn(
              styles.slider,
              { [styles.squareSliderLeft]: !stops && absMin && absMin !== min },
              {
                [styles.squareSliderRight]: !stops && absMax && absMax !== max,
              },
              inputProps.className,
            )}
            {...inputProps}
            value={internalValue}
            min={min}
            max={max}
            disabled={disabled}
            onChange={(ev) => {
              setInternalValue(parseFloat(ev.target.value));
              inputProps.onChange && inputProps.onChange(ev);
            }}
          />
          {stops && (
            <div ref={rightStop} className={cn(styles.stops, styles.max)}></div>
          )}
        </div>
        {absMax !== undefined && absMax !== max && (
          <div
            data-testid="fakeRightBar"
            style={{ width: `${fakeRightSize}%` }}
            className={cn(styles.fakeBar, styles.fakeRightBar)}
          />
        )}
        {rightLabel && (
          <span className={cn(labelClassName, rightLabelClassName)}>
            {rightLabel}
          </span>
        )}
      </div>
    );
  },
);

Slider.displayName = 'Range Input';
